import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, MenuItem, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import { Account } from '../../../interfaces/accounting.interface';
import {
    CorporateTaxRate, CorporateTaxRateRequest, CorporateTaxSettings, TAXPAYER_CATEGORIES, TaxpayerCategory,
} from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { humanizeEnum } from '../../../utils/format';
import { leafAccounts } from '../../../utils/postingAccounts';
import AccountAutocomplete from '../../common/AccountAutocomplete';
import { TAXPAYER_LABEL, currentIncomeYear, incomeYearLabel, incomeYearOptions } from './corporateTaxText';

interface Props {
    institutionId: string; settings: CorporateTaxSettings | null; accounts: Account[]; canManage: boolean; onSaved: () => void;
}

const cell = { flex: '1 1 260px', maxWidth: 380 } as const;

/** AIT account set, tax accounts, taxpayer category and the rate table (loaded as data, to be verified). */
const SettingsTab: React.FC<Props> = ({ institutionId, settings, accounts, canManage, onSaved }) => {
    const toast = useToast();
    const startMonth = settings?.fiscalYearStartMonth || 1;
    const postable = useMemo(() => leafAccounts(accounts), [accounts]);
    const [category, setCategory] = useState<TaxpayerCategory | ''>('');
    const [expense, setExpense] = useState('');
    const [payable, setPayable] = useState('');
    const [defExpense, setDefExpense] = useState('');
    const [defLiability, setDefLiability] = useState('');
    const [defAsset, setDefAsset] = useState('');
    const [aitIds, setAitIds] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [rates, setRates] = useState<CorporateTaxRate[]>([]);
    const [rateYear, setRateYear] = useState(currentIncomeYear(startMonth));
    const [newRate, setNewRate] = useState<CorporateTaxRateRequest>({
        incomeYear: currentIncomeYear(startMonth), category: 'NON_LISTED_COMPANY', regularRatePct: 0, minimumTaxRatePct: 0, exportSourceTaxFinal: false, note: '',
    });

    useEffect(() => {
        if (!settings) return;
        setCategory(settings.taxpayerCategory ?? '');
        setExpense(settings.currentTaxExpenseAccountId ?? '');
        setPayable(settings.incomeTaxPayableAccountId ?? '');
        setDefExpense(settings.deferredTaxExpenseAccountId ?? '');
        setDefLiability(settings.deferredTaxLiabilityAccountId ?? '');
        setDefAsset(settings.deferredTaxAssetAccountId ?? '');
        setAitIds(settings.aitAccounts.map(a => a.accountId));
    }, [settings]);

    const loadRates = useCallback(async () => {
        try { setRates(await CorporateTaxService.listRates(institutionId)); }
        catch (e) { toast.showApiError(e, 'Failed to load tax rates'); }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadRates(); }, [loadRates]);

    const save = async () => {
        setBusy(true);
        try {
            await CorporateTaxService.saveSettings(institutionId, {
                taxpayerCategory: category || null,
                currentTaxExpenseAccountId: expense || null,
                incomeTaxPayableAccountId: payable || null,
                deferredTaxExpenseAccountId: defExpense || null,
                deferredTaxLiabilityAccountId: defLiability || null,
                deferredTaxAssetAccountId: defAsset || null,
                aitAccountIds: aitIds,
            });
            toast.showSuccess('Corporate tax settings saved');
            onSaved();
        } catch (e) { toast.showApiError(e, 'Failed to save the settings'); }
        finally { setBusy(false); }
    };

    const detect = async () => {
        try {
            const found = await CorporateTaxService.detectAit(institutionId);
            if (found.length === 0) { toast.showSuccess('No AIT accounts were detected in the chart'); return; }
            setAitIds(Array.from(new Set([...aitIds, ...found.map(f => f.accountId)])));
            toast.showSuccess(`${found.length} AIT account(s) added to the selection: save to keep them`);
        } catch (e) { toast.showApiError(e, 'Failed to detect AIT accounts'); }
    };

    const createSuggested = async () => {
        setBusy(true);
        try {
            const r = await CorporateTaxService.createSuggestedAccounts(institutionId);
            toast.showSuccess(`Created ${r.created.length} account(s); ${r.existing.length} already existed`);
            onSaved();
        } catch (e) { toast.showApiError(e, 'Failed to create the suggested accounts'); }
        finally { setBusy(false); }
    };

    const loadDefaults = async () => {
        setBusy(true);
        try {
            const r = await CorporateTaxService.loadDefaults(institutionId, rateYear);
            toast.showSuccess(`Loaded ${r.added} rate(s), skipped ${r.skipped} existing. Verify them before posting.`);
            await loadRates();
        } catch (e) { toast.showApiError(e, 'Failed to load the default rates'); }
        finally { setBusy(false); }
    };

    const addRate = async () => {
        setBusy(true);
        try {
            await CorporateTaxService.createRate(institutionId, { ...newRate, note: newRate.note || null });
            toast.showSuccess('Rate added');
            await loadRates();
        } catch (e) { toast.showApiError(e, 'Failed to add the rate'); }
        finally { setBusy(false); }
    };

    const removeRate = async (id: string) => {
        try { await CorporateTaxService.deleteRate(institutionId, id); await loadRates(); }
        catch (e) { toast.showApiError(e, 'Failed to delete the rate'); }
    };

    const aitOptions = postable.filter(a => a.accountType === 'ASSET');
    const selectedAit = aitOptions.filter(a => a.id && aitIds.includes(a.id));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="subtitle1">Accounts and category</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField data-help="accounting.corpTax.set.category" select size="small" label="Taxpayer category" value={category} sx={cell} disabled={!canManage}
                    onChange={e => setCategory(e.target.value as TaxpayerCategory | '')}>
                    <MenuItem value="">Not set</MenuItem>
                    {TAXPAYER_CATEGORIES.map(c => <MenuItem key={c} value={c}>{TAXPAYER_LABEL[c]}</MenuItem>)}
                </TextField>
                <Box sx={cell}><AccountAutocomplete data-help="accounting.corpTax.set.expense" accounts={postable} value={expense} onChange={setExpense} label="Current tax expense" size="small" fullWidth disabled={!canManage} /></Box>
                <Box sx={cell}><AccountAutocomplete data-help="accounting.corpTax.set.payable" accounts={postable} value={payable} onChange={setPayable} label="Income tax payable" size="small" fullWidth disabled={!canManage} /></Box>
                <Box sx={cell}><AccountAutocomplete data-help="accounting.corpTax.set.defExpense" accounts={postable} value={defExpense} onChange={setDefExpense} label="Deferred tax expense" size="small" fullWidth disabled={!canManage} /></Box>
                <Box sx={cell}><AccountAutocomplete data-help="accounting.corpTax.set.defLiability" accounts={postable} value={defLiability} onChange={setDefLiability} label="Deferred tax liability" size="small" fullWidth disabled={!canManage} /></Box>
                <Box sx={cell}><AccountAutocomplete data-help="accounting.corpTax.set.defAsset" accounts={postable} value={defAsset} onChange={setDefAsset} label="Deferred tax asset" size="small" fullWidth disabled={!canManage} /></Box>
            </Box>

            <Typography variant="subtitle1">AIT / source-tax accounts</Typography>
            <Typography variant="body2" color="text.secondary">
                Every ledger line on these accounts is a tax credit. Import AIT, customer TDS, bank-interest AIT and advance tax all land here.
            </Typography>
            <Autocomplete data-help="accounting.corpTax.set.ait" multiple size="small" options={aitOptions} value={selectedAit} disabled={!canManage}
                getOptionLabel={a => `${a.code} ${a.name}`} isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, v) => setAitIds(v.map(a => a.id!).filter(Boolean))}
                renderInput={p => <TextField {...p} label="AIT accounts" />}
                renderTags={(v, getTagProps) => v.map((a, i) => <Chip {...getTagProps({ index: i })} key={a.id} size="small" label={`${a.code} ${a.name}`} />)} />
            {canManage && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="accounting.corpTax.set.save" variant="contained" disabled={busy} onClick={save}>Save settings</Button>
                    <Button data-help="accounting.corpTax.set.detect" onClick={detect}>Detect AIT accounts</Button>
                    <Button data-help="accounting.corpTax.set.createSuggested" disabled={busy} onClick={createSuggested}>Create suggested accounts</Button>
                </Box>
            )}

            <Typography variant="subtitle1">Tax rates</Typography>
            <Alert severity="warning">
                Loaded rates are the statutory defaults as understood when this was built. They are data, not law: verify each rate
                and the minimum-tax rule with your tax adviser for the year before posting anything.
            </Alert>
            {canManage && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField data-help="accounting.corpTax.set.rateYear" select size="small" label="Income year" value={rateYear} sx={{ flex: '1 1 180px', maxWidth: 240 }}
                        onChange={e => setRateYear(Number(e.target.value))}>
                        {incomeYearOptions(startMonth).map(y => <MenuItem key={y} value={y}>{incomeYearLabel(y, startMonth)}</MenuItem>)}
                    </TextField>
                    <Button data-help="accounting.corpTax.set.loadDefaults" disabled={busy} onClick={loadDefaults}>Load Bangladesh defaults (verify)</Button>
                </Box>
            )}
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.set.rates" size="small" aria-label="Tax rates">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.corpTax.set.col.year">Income year</TableCell><TableCell data-help="accounting.corpTax.set.col.category">Category</TableCell><TableCell data-help="accounting.corpTax.set.col.regular" align="right">Regular %</TableCell>
                            <TableCell data-help="accounting.corpTax.set.col.minimum" align="right">Minimum % of receipts</TableCell><TableCell data-help="accounting.corpTax.set.col.export">Export tax final</TableCell>
                            <TableCell data-help="accounting.corpTax.set.col.origin">Origin</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rates.map(r => (
                            <TableRow key={r.id}>
                                <TableCell>{incomeYearLabel(r.incomeYear, startMonth)}</TableCell>
                                <TableCell>{TAXPAYER_LABEL[r.category]}</TableCell>
                                <TableCell align="right">{r.regularRatePct}</TableCell>
                                <TableCell align="right">{r.minimumTaxRatePct}</TableCell>
                                <TableCell>{r.exportSourceTaxFinal ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{r.origin === 'DEFAULT' ? 'Default (verify)' : humanizeEnum(r.origin)}</TableCell>
                                <TableCell>{canManage && <IconButton data-help="accounting.corpTax.set.action.delete" size="small" aria-label="Delete rate" onClick={() => removeRate(r.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>}</TableCell>
                            </TableRow>
                        ))}
                        {rates.length === 0 && <TableRow><TableCell colSpan={7}>No rates yet. Load the defaults or add one below.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            {canManage && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField data-help="accounting.corpTax.set.newYear" select size="small" label="Income year" value={newRate.incomeYear} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        onChange={e => setNewRate({ ...newRate, incomeYear: Number(e.target.value) })}>
                        {incomeYearOptions(startMonth).map(y => <MenuItem key={y} value={y}>{incomeYearLabel(y, startMonth)}</MenuItem>)}
                    </TextField>
                    <TextField data-help="accounting.corpTax.set.newCategory" select size="small" label="Category" value={newRate.category} sx={{ flex: '1 1 200px', maxWidth: 280 }}
                        onChange={e => setNewRate({ ...newRate, category: e.target.value as TaxpayerCategory })}>
                        {TAXPAYER_CATEGORIES.map(c => <MenuItem key={c} value={c}>{TAXPAYER_LABEL[c]}</MenuItem>)}
                    </TextField>
                    <TextField data-help="accounting.corpTax.set.newRegular" size="small" label="Regular %" type="number" value={newRate.regularRatePct} sx={{ flex: '1 1 120px', maxWidth: 160 }}
                        onChange={e => setNewRate({ ...newRate, regularRatePct: Number(e.target.value) })} />
                    <TextField data-help="accounting.corpTax.set.newMinimum" size="small" label="Minimum %" type="number" value={newRate.minimumTaxRatePct} sx={{ flex: '1 1 120px', maxWidth: 160 }}
                        onChange={e => setNewRate({ ...newRate, minimumTaxRatePct: Number(e.target.value) })} />
                    <FormControlLabel data-help="accounting.corpTax.set.newExport" label="Export tax final" control={<Checkbox checked={newRate.exportSourceTaxFinal}
                        onChange={e => setNewRate({ ...newRate, exportSourceTaxFinal: e.target.checked })} />} />
                    <Button data-help="accounting.corpTax.set.addRate" disabled={busy} onClick={addRate}>Add rate</Button>
                </Box>
            )}
        </Box>
    );
};

export default SettingsTab;
